/**
 * WorkspaceSelector — Mercato-flavoured chip showing the active workspace
 * with a click-to-switch dropdown.
 *
 * Wave 7 W7-1: ships the minimal but functional surface so multi-workspace
 * users can pick which tenant they operate against. The active workspace_id
 * is persisted as a same-domain cookie (`x-commerco-workspace-id`) that the
 * tRPC client forwards on every request — `createContext` validates it
 * against `workspace_members` for the current user, so a forged value cannot
 * grant cross-tenant access.
 */
"use client";

import * as React from "react";
import { useRouter } from "next/navigation";

import { cn } from "@commerco/mercato";

export interface WorkspaceOption {
  id: string;
  name: string;
  role: string;
}

const WORKSPACE_COOKIE = "commerco_active_workspace";

function setWorkspaceCookie(workspaceId: string): void {
  // Wave 7: same-site Lax + 30-day persistence + path=/ so every API route
  // sees it. Not HttpOnly — the tRPC client must read it to forward as
  // x-commerco-workspace-id header.
  const maxAge = 60 * 60 * 24 * 30;
  document.cookie =
    `${WORKSPACE_COOKIE}=${encodeURIComponent(workspaceId)};` +
    ` Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

export function WorkspaceSelector(props: {
  current: { id: string; name: string; role: string } | null;
  options: WorkspaceOption[];
}): React.JSX.Element {
  const { current, options } = props;
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function switchTo(workspaceId: string) {
    setWorkspaceCookie(workspaceId);
    setOpen(false);
    router.refresh();
  }

  if (!current) {
    return (
      <div
        className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1 text-[var(--text-body-sm)] text-[var(--color-fg-tertiary)]"
        aria-label="No workspace selected"
      >
        Çalışma alanı seçilmedi
      </div>
    );
  }

  const switchable = options.filter((o) => o.id !== current.id);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "flex items-center gap-2 rounded-full border px-3 py-1.5",
          "border-[var(--color-border)] bg-[var(--color-bg-surface)]",
          "text-[var(--text-body-sm)] text-[var(--color-fg-primary)]",
          "hover:bg-[var(--color-bg-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]",
        )}
        data-component="workspace-chip"
      >
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full bg-[var(--color-accent)]"
        />
        <span className="font-medium">{current.name}</span>
        <span className="text-[var(--color-fg-tertiary)]">· {current.role}</span>
        {switchable.length > 0 ? (
          <span aria-hidden="true" className="ml-1 text-[var(--color-fg-tertiary)]">
            ▾
          </span>
        ) : null}
      </button>

      {open && switchable.length > 0 ? (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-lg"
        >
          {switchable.map((opt) => (
            <li key={opt.id} role="option" aria-selected="false">
              <button
                type="button"
                onClick={() => switchTo(opt.id)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-[var(--text-body-sm)] text-[var(--color-fg-primary)] hover:bg-[var(--color-bg-hover)]"
              >
                <span>{opt.name}</span>
                <span className="text-[var(--color-fg-tertiary)]">
                  {opt.role}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
